// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { AccordionSectionPanel } from "@insite/mobius/AccordionSection";
import { AccordionSectionHeaderProps } from "@insite/mobius/AccordionSection/AccordionSectionHeader";
import AccordionSectionPresentationPropsDefault from "@insite/mobius/AccordionSection/presentationProps";
import { fontConfig } from "@vanmeter/XDL/constants";
import MinusCircle from "@vanmeter/XDL/Icons/IconMinuCircle";
import Minus from "@vanmeter/XDL/Icons/IconMinus";
import Plus from "@vanmeter/XDL/Icons/IconPlus";
import PlusCircle from "@vanmeter/XDL/Icons/IconPlusCircle";
import { xBaseTheme } from "@vanmeter/XDL/types";
import { color } from "@vanmeter/XDL/Utilities";
import { css } from "styled-components";

export const accordionSectionSmall = {
    ...AccordionSectionPresentationPropsDefault,
    toggleIconProps: {
        color: "primary",
        height: 13,
        width: 12,
        css: css`
            &:hover {
                color: ${color("common.ui15")};
            }
        `,
    },
    expandIconProps: {
        src: PlusCircle,
    },
    collapseIconProps: {
        src: MinusCircle,
    },

    titleTypographyProps: {
        ...fontConfig.xSmallSingleLine,
        weight: fontConfig.weight.bold,
        fontFamily: fontConfig.fontFamily,
        color: "text",
        css: css`
            letter-spacing: 0;
            margin-bottom: 0;
        `,
    },
    panelProps: {
        css: css`
            padding: 0 8px 8px;
            border: none;
            &:last-of-type {
                border-bottom: 1px solid ${color("common.border")};
            }
            font-size: ${fontConfig.xSmallSingleLine.size}px;
            line-height: ${fontConfig.xSmallSingleLine.lineHeight};
            letter-spacing: 0;
        `,
    },
    headerProps: {
        css: css`
            border-top: 1px solid ${color("common.border")};
            &:last-of-type {
                border-bottom: 1px solid ${color("common.border")};
            }
            ${({ expanded }: AccordionSectionHeaderProps) =>
                expanded &&
                css`
                    &:last-of-type {
                        border-bottom: none;
                    }
                `}
            &&& button,
            &&& button:focus {
                margin-bottom: 0;
                padding: 8px;
                border: none;
                background: ${color("common.ui1")};
            }
        `,
    },
} as xBaseTheme["accordion"]["sectionDefaultProps"];

export default {
    defaultProps: {
        mergeCss: true,
    },
    sectionDefaultProps: {
        ...AccordionSectionPresentationPropsDefault,
        mergeCss: true,
        titleTypographyProps: {
            variant: "h4",
            forwardAs: "span",
            color: "common.ui1",
            css: css`
                letter-spacing: 0;
                margin-bottom: 0;
            `,
        },
        toggleIconProps: {
            color: "common.ui1",
            width: 21,
            height: 24,
            css: css`
                &:hover {
                    color: ${color("secondary")};
                }
            `,
        },
        expandIconProps: {
            src: Plus,
        },
        collapseIconProps: {
            src: Minus,
        },
        headerProps: {
            css: css`
                button {
                    padding: 8px 12px;
                    background: ${color("primary")};
                    border: none;
                }

                &&& button,
                &&& ${/* sc-selector */ AccordionSectionPanel} + & button {
                    &:focus {
                        padding: 8px 12px;
                        border: none;
                        outline-width: 0;
                    }
                }

                ${({ expanded }: AccordionSectionHeaderProps) =>
                    !expanded &&
                    css`
                        &&& button,
                        &&& button:focus {
                            margin-bottom: 32px;
                        }
                        &&& button,
                        &&& button:focus,
                        ${AccordionSectionPanel} + & button:focus {
                            border-bottom-color: ${color("common.border")};
                        }
                    `}
            `,
        },
        panelProps: {
            css: css`
                border: none;
                padding: 12px;
                font-size: ${fontConfig.regular.size}px;
                line-height: ${fontConfig.regularSingleLine.lineHeight};
                letter-spacing: 0;
            `,
        },
    },
} as xBaseTheme["accordion"];
